import React from "react";
import classnames from "classnames";
import { usePagination, ellipse } from "./hooks/usePagination";
import { EndCap, EllipseButton, NumberButton } from "./components";
import styles from "./pagination.module.scss";

export type PaginationProps = {
  /**
   * This is a callback function prop that returns the currently selected page as the argument. Can be used to fire new actions whenever a new page is selected.
   */
  onPageChange: (page: number) => void;
  /**
   * The total number of pages to display
   */
  totalCount: number;
  /**
   * The current page selected.
   */
  currentPage?: number;
  /**
   * Controls the size of the pagination component
   */
  size?: "default" | "small";
} & React.HTMLAttributes<HTMLUListElement>;

const Pagination = ({
  onPageChange,
  totalCount,
  currentPage = 1,
  size = "default",
  ...props
}: PaginationProps) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    size,
  });

  const onNext = () => onPageChange(currentPage + 1);

  const onPrevious = () => onPageChange(currentPage - 1);

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <ul {...props} className={classnames(styles.pagination, props.className)}>
      <li>
        <EndCap
          size={size}
          direction="back"
          disabled={currentPage === 1}
          onClick={onPrevious}
        />
      </li>
      {paginationRange.map((pageNumber, index) => (
        <li key={index} className={styles.page}>
          {pageNumber === ellipse ? (
            <EllipseButton size={size} />
          ) : (
            <NumberButton
              active={pageNumber === currentPage}
              size={size}
              onClick={onPageChange}
            >
              {pageNumber as number}
            </NumberButton>
          )}
        </li>
      ))}
      <li>
        <EndCap
          size={size}
          direction="forward"
          disabled={currentPage === lastPage}
          onClick={onNext}
        />
      </li>
    </ul>
  );
};

export default Pagination;
